import type { NewProductState } from "./state";
import { SCORABLE_FIELDS, type ScorableField } from "./qualityApi";

export interface FieldEvaluationInput {
    field: ScorableField;
    value: string;
    dependencyKey: string;
}

function canonicalList(values: string[]): string[] {
    return values.map(value => value.trim()).filter(Boolean);
}

export function fieldEvaluationValue(state: NewProductState, field: ScorableField): string {
    if (field === "personas") return canonicalList(state.users).join(", ");
    if (field === "existing_purpose") return state.existingProduct.purpose.trim();
    if (field === "existing_capabilities") return state.existingProduct.capabilities.trim();
    if (field === "existing_users") return canonicalList(state.existingProduct.users).join(", ");
    return state.fields[field].trim();
}

/** Canonical dependencies mirror ContextMeshFieldQualityService._evaluation_context.
 * Keeping this in one frontend function ensures manual edits, inference, list
 * mutations, resumed drafts, bulk patches, and kind changes all invalidate the
 * same downstream fields. The backend remains authoritative for the hashes. */
export function fieldEvaluationDependencyKey(state: NewProductState, field: ScorableField): string {
    const existingProduct = state.kind === "existing"
        ? {
            purpose: state.existingProduct.purpose.trim(),
            capabilities: state.existingProduct.capabilities.trim(),
            users: canonicalList(state.existingProduct.users)
        }
        : null;

    if (field === "existing_purpose" || (field === "problem" && existingProduct === null)) return "";
    if (field === "existing_capabilities") {
        return JSON.stringify({ purpose: existingProduct?.purpose ?? "" });
    }
    if (field === "existing_users") return JSON.stringify(existingProduct);
    if (field === "problem") return JSON.stringify(existingProduct);
    if (field === "solution") {
        return JSON.stringify({ existingProduct, problem: state.fields.problem.trim() });
    }
    return JSON.stringify({
        existingProduct,
        problem: state.fields.problem.trim(),
        solution: state.fields.solution.trim()
    });
}

export function applicableEvaluationFields(
    state: NewProductState,
    options: { productRefresh?: boolean } = {}
): readonly ScorableField[] {
    if (options.productRefresh || state.kind !== "existing") {
        return SCORABLE_FIELDS.slice(0, 3);
    }
    return SCORABLE_FIELDS;
}

export function buildFieldEvaluationPlan(
    state: NewProductState,
    options: { productRefresh?: boolean } = {}
): FieldEvaluationInput[] {
    return applicableEvaluationFields(state, options).map(field => ({
        field,
        value: fieldEvaluationValue(state, field),
        dependencyKey: options.productRefresh ? "" : fieldEvaluationDependencyKey(state, field)
    }));
}

export function fieldEvaluationCacheKey(value: string, dependencyKey: string): string {
    return `${value.trim()}\u0000${dependencyKey}`;
}
